import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Button, Input, Space, Tag, message, Avatar, Typography, Popconfirm } from 'antd';
import {
  MobileOutlined, CheckCircleFilled, MessageOutlined,
} from '@ant-design/icons';

const { Text } = Typography;

const API_BASE = process.env.REACT_APP_API_BASE || '';
const BRAND = 'var(--brand-primary, #222957)';
const BORDER = '#E5E7F5';
const HEADING = '#222A5A';
const LINE_GREEN = '#06c755';

const getToken = () => {
  try { return localStorage.getItem('customer_token'); } catch { return null; }
};

// 會員帳號綁定：手機簡訊驗證（P2）＋ LINE 綁定（P3）。共用一次 /customer/profile。
export default function AccountBinding() {
  const [profile, setProfile] = useState(null);
  const [verifyEnabled, setVerifyEnabled] = useState(false); // 簡訊 OTP 驗證是否啟用（後台可切）

  // 手機驗證狀態
  const [phone, setPhone] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const timerRef = useRef(null);

  const loadProfile = useCallback(() => {
    const token = getToken();
    if (!token) return;
    fetch(`${API_BASE}/api/customer/profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) { setProfile(d); setPhone((p) => p || d.phone || ''); } })
      .catch(() => {});
  }, []);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  // 讀取後台設定：手機驗證是否啟用（關閉時改為直接儲存、不發簡訊）
  useEffect(() => {
    fetch(`${API_BASE}/api/settings/shop`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setVerifyEnabled(!!d.phone_verify_enabled); })
      .catch(() => {});
  }, []);

  // 處理 LINE 綁定導回結果
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get('line_bound')) {
      message.success('LINE 綁定成功');
    } else if (q.get('line_error')) {
      const map = {
        not_configured: 'LINE 綁定尚未開放', login_required: '請先登入再綁定',
        already_bound: '此 LINE 帳號已綁定其他會員', invalid_state: '綁定連結已失效，請重試',
        exchange_failed: 'LINE 授權失敗，請重試', no_userid: '無法取得 LINE 身分',
      };
      message.error(map[q.get('line_error')] || 'LINE 綁定失敗');
    }
    if (q.get('line_bound') || q.get('line_error')) {
      q.delete('line_bound'); q.delete('line_error');
      const rest = q.toString();
      window.history.replaceState(null, '', window.location.pathname + (rest ? `?${rest}` : ''));
    }
  }, []);

  const startCooldown = (sec) => {
    setCooldown(sec);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) { clearInterval(timerRef.current); return 0; }
        return c - 1;
      });
    }, 1000);
  };
  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  const sendCode = async () => {
    const token = getToken();
    if (!/^09\d{8}$/.test(phone.trim())) { message.warning('請輸入正確手機號碼（09 開頭共 10 碼）'); return; }
    setSending(true);
    try {
      const res = await fetch(`${API_BASE}/api/customer/phone/send-code`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ phone: phone.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { message.error(data.detail || '發送失敗'); return; }
      message.success('驗證碼已發送，請查看簡訊');
      setCodeSent(true);
      startCooldown(data.cooldown || 60);
    } catch {
      message.error('發送失敗，請稍後再試');
    } finally {
      setSending(false);
    }
  };

  const savePhone = async () => {
    const token = getToken();
    if (!/^09\d{8}$/.test(phone.trim())) { message.warning('請輸入正確手機號碼（09 開頭共 10 碼）'); return; }
    setSending(true);
    try {
      const res = await fetch(`${API_BASE}/api/customer/phone/save`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ phone: phone.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { message.error(data.detail || '儲存失敗'); return; }
      message.success('手機號碼已儲存');
      loadProfile();
    } catch {
      message.error('儲存失敗，請稍後再試');
    } finally {
      setSending(false);
    }
  };

  const verify = async () => {
    const token = getToken();
    if (!code.trim()) { message.warning('請輸入驗證碼'); return; }
    setVerifying(true);
    try {
      const res = await fetch(`${API_BASE}/api/customer/phone/verify`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ phone: phone.trim(), code: code.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { message.error(data.detail || '驗證失敗'); return; }
      message.success('手機驗證成功');
      setCodeSent(false); setCode('');
      loadProfile();
    } catch {
      message.error('驗證失敗，請稍後再試');
    } finally {
      setVerifying(false);
    }
  };

  const bindLine = () => {
    const token = getToken();
    window.location.href = `${API_BASE}/api/auth/line/bind/login?ctoken=${encodeURIComponent(token || '')}`;
  };

  const unbindLine = async () => {
    const token = getToken();
    try {
      const res = await fetch(`${API_BASE}/api/auth/line/unbind`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { message.error('解除失敗'); return; }
      message.success('已解除 LINE 綁定');
      loadProfile();
    } catch {
      message.error('解除失敗，請稍後再試');
    }
  };

  const verified = profile?.phone_verified;

  return (
    <Card
      title={<span style={{ fontWeight: 800, color: BRAND }}>帳號綁定</span>}
      style={{ borderRadius: 16, borderColor: BORDER }}
      styles={{ body: { padding: 16 } }}
    >
      {/* 手機驗證 */}
      <div style={{ marginBottom: 18 }}>
        <Space size={8} style={{ marginBottom: 8 }}>
          <MobileOutlined style={{ color: BRAND }} />
          <Text strong style={{ color: HEADING }}>手機號碼</Text>
          {verifyEnabled
            ? (verified ? <Tag icon={<CheckCircleFilled />} color="success">已驗證</Tag> : <Tag color="default">未驗證</Tag>)
            : (profile?.phone ? <Tag color="blue">已儲存</Tag> : <Tag color="default">未填寫</Tag>)}
        </Space>
        <Space.Compact style={{ width: '100%' }}>
          <Input
            placeholder="09xxxxxxxx" inputMode="tel" value={phone}
            onChange={(e) => setPhone(e.target.value)} disabled={verifyEnabled && verified}
          />
          {verifyEnabled ? (
            <Button onClick={sendCode} loading={sending} disabled={verified || cooldown > 0}>
              {cooldown > 0 ? `${cooldown}s` : (verified ? '已驗證' : '發送驗證碼')}
            </Button>
          ) : (
            <Button type="primary" onClick={savePhone} loading={sending}>儲存</Button>
          )}
        </Space.Compact>
        {verifyEnabled && codeSent && !verified && (
          <Space.Compact style={{ width: '100%', marginTop: 8 }}>
            <Input placeholder="輸入 6 位數驗證碼" inputMode="numeric" maxLength={6}
              value={code} onChange={(e) => setCode(e.target.value)} onPressEnter={verify} />
            <Button type="primary" onClick={verify} loading={verifying}>驗證</Button>
          </Space.Compact>
        )}
        {!verifyEnabled && (
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
            手機號碼將用於結帳與物流聯繫（目前免簡訊驗證）。
          </Text>
        )}
      </div>

      {/* LINE 綁定 */}
      <div style={{ borderTop: '1px dashed #eee', paddingTop: 14 }}>
        <Space size={8} style={{ marginBottom: 8 }}>
          <MessageOutlined style={{ color: LINE_GREEN }} />
          <Text strong style={{ color: HEADING }}>LINE 帳號</Text>
          {profile?.line_bound ? <Tag color="success">已綁定</Tag> : <Tag color="default">未綁定</Tag>}
        </Space>
        {profile?.line_bound ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <Space>
              <Avatar src={profile.line_picture || undefined} size={36}>
                {profile.line_display_name ? profile.line_display_name.charAt(0) : 'L'}
              </Avatar>
              <Text>{profile.line_display_name || '已綁定'}</Text>
            </Space>
            <Popconfirm title="解除 LINE 綁定？" okText="解除" cancelText="取消" onConfirm={unbindLine}>
              <Button size="small">解除綁定</Button>
            </Popconfirm>
          </div>
        ) : (
          <Button
            block icon={<MessageOutlined />} onClick={bindLine}
            style={{ background: LINE_GREEN, color: '#fff', border: 'none', height: 42, fontWeight: 700 }}
          >
            綁定 LINE 帳號（接收訂單與物流通知）
          </Button>
        )}
      </div>
    </Card>
  );
}
